import { z } from "zod";
import {
  isoDateTimeString,
  latitudeSchema,
  longitudeSchema,
  uuidSchema,
} from "./common";
import { PROVINCE_CODES, SUPPORTED_PROVINCE_CODES } from "../catalogs";

export const ORG_ADDRESS_ALIAS_MAX = 60;
export const ORG_ADDRESS_STREET_MAX = 255;

export const orgAddressSchema = z.object({
  id: uuidSchema,
  orgId: uuidSchema,
  alias: z.string(),
  address: z.string(),
  district: z.string(),
  city: z.string(),
  // Legacy province enum — nullable now (only resolves for HCM; nationwide
  // addresses carry their province in wardProvinceCode).
  provinceCode: z.enum(PROVINCE_CODES).nullable(),
  // 2025 post-merger ward. Nullable on legacy rows (pre-migration) — that null
  // is what the "needs address update" banner keys off. New rows always carry
  // them. `wardProvinceCode` is the official NEW numeric province code string.
  wardCode: z.string().nullable(),
  wardName: z.string().nullable(),
  wardProvinceCode: z.string().nullable(),
  lat: z.number(),
  lng: z.number(),
  // Canonical Google Maps URL — mobile uses this to open the branded
  // place pin. Null on legacy rows / non-Google sources.
  mapUrl: z.string().url().nullable(),
  createdAt: isoDateTimeString,
  updatedAt: isoDateTimeString,
  archivedAt: isoDateTimeString.nullable().optional(),
  // Count of OPEN postings currently referencing this address. Drives the
  // "đang dùng" badge + locks edit/archive on the FE.
  inUseByOpenPostings: z.number().int().nonnegative(),
});

export const orgAddressListResponseSchema = z.object({
  items: z.array(orgAddressSchema),
});

export const createOrgAddressRequestSchema = z.object({
  alias: z
    .string()
    .min(1, "Nhập tên gợi nhớ")
    .max(ORG_ADDRESS_ALIAS_MAX, `Tối đa ${ORG_ADDRESS_ALIAS_MAX} ký tự`),
  address: z.string().min(1).max(ORG_ADDRESS_STREET_MAX),
  // Legacy admin fields — kept optional so old callers/imports still work, but
  // the new flow no longer requires them (ward replaces district).
  district: z.string().max(60).optional().default(""),
  city: z.string().max(60).optional().default(""),
  // Legacy province enum, now optional+nullable (HCM-only; app is nationwide
  // via wardProvinceCode).
  provinceCode: z.enum(SUPPORTED_PROVINCE_CODES).nullable().optional(),
  // 2025 ward — REQUIRED on new addresses (decision: bắt buộc chọn phường).
  // `wardCode`/`wardName` come from point-in-polygon auto-fill or the user's
  // manual pick; `wardProvinceCode` is the official new numeric province code.
  wardCode: z.string().min(1, "Vui lòng chọn phường / xã").max(20),
  wardName: z.string().min(1).max(120),
  wardProvinceCode: z.string().min(1).max(10),
  lat: latitudeSchema,
  lng: longitudeSchema,
  // Set by the import pipeline when coords are a placeholder fallback (failed
  // geocode). Employer-entered addresses leave this false. NEAREST feed sorts
  // approx addresses last and hides their distance.
  geoApprox: z.boolean().optional(),
  mapUrl: z.string().url().nullable().optional(),
});

export const updateOrgAddressRequestSchema =
  createOrgAddressRequestSchema.partial();

// Bulk-add branches from Google Maps links (SCRUM-73, spec §5.5). Each row is a
// link + optional alias; the server geocodes each via parseGoogleMapsUrl and
// creates an OrgAddress. The whole batch is checked against the legal-type
// address cap up front (all-or-nothing on the cap).
export const orgAddressBulkRequestSchema = z.object({
  items: z
    .array(
      z.object({
        mapUrl: z.string().url("Link Google Maps không hợp lệ"),
        alias: z.string().max(ORG_ADDRESS_ALIAS_MAX).optional(),
      }),
    )
    .min(1)
    .max(20),
});

// Per-row outcome so the UI can show which links resolved and which failed.
export const orgAddressBulkResponseSchema = z.object({
  created: z.array(orgAddressSchema),
  failed: z.array(
    z.object({
      mapUrl: z.string(),
      error: z.string(),
    }),
  ),
});

export type OrgAddress = z.infer<typeof orgAddressSchema>;
export type OrgAddressListResponse = z.infer<
  typeof orgAddressListResponseSchema
>;
export type CreateOrgAddressRequest = z.infer<
  typeof createOrgAddressRequestSchema
>;
export type OrgAddressBulkRequest = z.infer<typeof orgAddressBulkRequestSchema>;
export type OrgAddressBulkResponse = z.infer<
  typeof orgAddressBulkResponseSchema
>;
export type UpdateOrgAddressRequest = z.infer<
  typeof updateOrgAddressRequestSchema
>;
